import BaseListPresenter from "../../base/BaseListPresenter";
import { getCurrentUserUseCase } from "../../usecases/user";
import {
  format,
  startOfDay,
  addHours,
  addMinutes,
  addDays,
  parseISO,
  formatISO,
} from "date-fns";
import xhr from "../../usecases/xhr/Xhr";

class ProductsPresenter extends BaseListPresenter {
  constructor(view, findObjectUseCase, countObjectUseCase) {
    super(view, findObjectUseCase, countObjectUseCase);
    this.objects = [];
    this.abortController = null; // Initialize abort controller
  }
  init() {
    this.limit = 50000;
    this.where = {};
    this.search = {};
    this.searchSync = false;
    this.searchAny = null;
    this.filter = {};
    this.filterDate = {};
    this.include = ["all"];
    this.filterStore = {};
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  componentDidMount() {
    this.init();
    this.findStore();
    return this.getObjects();
  }

  componentWillUnmount() {
    this.cleanup();
  }

  async getObjects() {
    this.reset();
    this.find();
  }

  async findStore() {
    const user = await getCurrentUserUseCase().execute();
    const storeQuery = { include: ["all"], where: { email: user.email } };
    this.findObjectUseCase.abort();
    this.store = await this.findObjectUseCase.execute("users", storeQuery);

    const store = (this.store?.[0]?.storeAnalytic ?? []).sort((a, b) =>
      a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
    );
    this.view.setStore(store);
  }

  async find() {
    try {
      if (this.abortController && !this.abortController.signal.aborted) {
        this.abortController.abort();
        console.log("Aborted previous requests");
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      this.abortController = new AbortController();
      this.objects = [];
      const startTime = Date.now();

      const query = this.createQuery({ skip: 0, limit: this.limit });
      delete query.limit;
      delete query.include;

      const startDate = query.where?.tran_date?.$gte || "";
      const endDate = query.where?.tran_date?.$lte || "";

      const dateRanges = this.generateDateRanges(startDate, endDate);

      for (let i = 0; i < dateRanges.length; i++) {
        const range = dateRanges[i];

        const chunkQuery = {
          ...query,
          where: {
            ...query.where,
            tran_date: {
              $gte: range.start,
              $lte: range.end,
            },
          },
        };

        console.log("chunkQuery", chunkQuery);

        try {
          const chunkData = await xhr.get("/transactions", {
            params: chunkQuery,
            signal: this.abortController.signal,
          });

          this.objects = this.objects.concat(chunkData);
          this.view.setObjects(this.objects);
        } catch (chunkError) {
          if (chunkError.name === "AbortError") {
            console.log("Request was aborted at chunk", i + 1);
            return;
          }
          console.error(`Error fetching ${range.label}:`, chunkError.message);
        }
      }

      const downloadTime = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log(
        `✅ Complete! ${this.objects.length.toLocaleString()} records in ${downloadTime}s`,
      );
      this.abortController = null;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Entire operation was aborted");
        return;
      }
      console.error("Error:", error.message);
    }
  }

  cleanup() {
    if (this.abortController) {
      console.log("🧹 Cleaning up...");
      this.abortController.abort();
      this.abortController = null;
    }
    this.objects = [];
  }

  // Simple range generator: ≤31 days = 1 request, >31 days = monthly chunks
  generateDateRanges(startDateStr, endDateStr) {
    const start = new Date(startDateStr.split(" ")[0]);
    const end = new Date(endDateStr.split(" ")[0]);

    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

    console.log(`📆 Date range: ${daysDiff} days`);

    // If 31 days or less, single request
    if (daysDiff <= 31) {
      console.log("Strategy: Single request (≤31 days)");
      return [
        {
          start: this.formatDate(start),
          end: this.formatEndDate(end),
          label: `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
        },
      ];
    }

    // More than 31 days, use monthly chunks
    console.log("Strategy: Monthly chunks (>31 days)");
    return this.generateMonthlyRanges(start, end);
  }

  // Generate monthly ranges
  // generateMonthlyRanges(startDate, endDate) {
  //   const ranges = [];
  //   let current = new Date(startDate);

  //   while (current < endDate) {
  //     const monthStart = new Date(current);
  //     const monthEnd = new Date(
  //       current.getFullYear(),
  //       current.getMonth() + 1,
  //       0,
  //       23,
  //       59,
  //       59,
  //     );

  //     if (monthEnd > endDate) {
  //       monthEnd.setTime(endDate.getTime());
  //     }

  //     ranges.push({
  //       start: this.formatDate(monthStart),
  //       end: this.formatEndDate(monthEnd),
  //       label: monthStart.toLocaleString("default", {
  //         month: "short",
  //         year: "numeric",
  //       }),
  //     });

  //     current.setMonth(current.getMonth() + 1);
  //   }

  //   return ranges;
  // }
  generateMonthlyRanges(startDate, endDate) {
    const ranges = [];
    let current = new Date(startDate);

    while (current < endDate) {
      const monthStart = new Date(current);
      const monthEnd = new Date(
        current.getFullYear(),
        current.getMonth() + 1,
        0,
        23,
        59,
        59,
      );

      if (monthEnd > endDate) {
        monthEnd.setTime(endDate.getTime());
      }

      // ADD +1 day dito para sa business day
      const businessDayEnd = new Date(monthEnd);
      businessDayEnd.setDate(businessDayEnd.getDate() + 1);

      ranges.push({
        start: this.formatDate(monthStart),
        end: this.formatEndDate(businessDayEnd), // Use businessDayEnd
        label: monthStart.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
      });

      current.setMonth(current.getMonth() + 1);
    }

    return ranges;
  }

  // Format date for API
  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day} 05:00:00.000`;
  }

  // formatEndDate(date) {
  //   const year = date.getFullYear();
  //   const month = String(date.getMonth() + 1).padStart(2, "0");
  //   const day = String(date.getDate()).padStart(2, "0");
  //   return `${year}-${month}-${day} 04:59:59.999`;
  // }

  formatEndDate(date) {
    // Remove the +1 day
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day} 04:59:59.999`;
  }

  createQuery({ skip = 0, limit = this.limit } = {}) {
    const storeCodes =
      this.store?.flatMap((s) => s.storeAnalytic?.map((a) => a?.name)) ?? [];

    const escapedSearch = this.searchAny
      ?.trim()
      ?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const whereClause = {
      ...(this.where || {}),
      ...(this.filter || {}),
      ...(this.filterDate || {}),
    };

    if (this.filterStore === "AllStore") {
      if (storeCodes.length > 0) {
        whereClause.store_code = { $in: storeCodes };
      }
    } else if (this.filterStore) {
      whereClause.store_code = this.filterStore;
    }

    // 3) Build final query
    const query = {
      limit,
      skip,
      where: whereClause,
      include: this.include,
      keys: this.keys ?? [
        "items",
        "status",
        "discount_amount",
        "total_taxfree",
        "store_code",
        "store_id",
        "tran_id",
        "paymenttype",
        "paymenttypetotal",
        "paymenttype2",
        "paymenttype2total",
        "paymenttype3",
        "paymenttype3total",
        "paymenttypetotal",
        "tran_type",
      ],
      sort: this.sort,
    };

    return query;
  }

  async countObjects() {
    try {
      this.showProgress();
      const collection = this.view.getCollectionName();
      const query = this.createQuery();
      const { count } = await this.countObjectUseCase.execute(collection, {
        where: query.where,
      });
      this.count = count;
      console.log("this.count", this.count);
      // this.view.setCount(this.count);
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
  }

  // async findObjects() {
  //   const collection = this.view.getCollectionName();

  //   try {
  //     // 1) Load store first (needed by createQuery)
  //     const user = await getCurrentUserUseCase().execute();
  //     const storeQuery = { include: ["all"], where: { email: user.email } };
  //     this.store = await this.findObjectUseCase.execute("users", storeQuery);

  //     const store = (this.store?.[0]?.storeAnalytic ?? []).sort((a, b) =>
  //       a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
  //     );
  //     this.view.setStore(store);

  //     this.showProgress();
  //     this.view.setNotFound(false);
  //     this.findObjectUseCase.abort(); // cancel any in-flight

  //     // 2) Get total count with the full filter (skip=0)
  //     const baseQuery = this.createQuery({ skip: 0, limit: this.limit });
  //     const { count } = await this.countObjectUseCase.execute(collection, {
  //       where: baseQuery.where,
  //     });
  //     this.count = count;
  //     console.log("count", count);
  //     this.view.setCount(count);

  //     // Reset UI holders
  //     this.objects = [];
  //     this.view.setObjects([]);
  //     this.view.setTotal(0);

  //     // Gate fetches (you already use this flag)
  //     if (this.searchSync === false) {
  //       console.log("Stopped before fetching (searchSync=false).");
  //       return;
  //     }

  //     // if (count === 0) {
  //     //   // Nothing to fetch
  //     //   this.view.setNotFound(true);
  //     //   return;
  //     // }

  //     // 3) Batch pagination loop (20 per batch by default)
  //     let fetched = 0;
  //     let page = 0;

  //     // Optional: smooth UI updates
  //     let buffer = [];
  //     const flushBuffer = () => {
  //       if (!buffer.length) return;
  //       this.objects = this.objects.concat(buffer);
  //       this.view.setObjects(this.objects);
  //       this.view.setTotal(this.objects.length);
  //       buffer = [];
  //     };
  //     const flushInterval = setInterval(flushBuffer, 500);

  //     while (fetched < count) {
  //       if (this.searchSync === false) {
  //         console.log("Stopped mid-fetch (searchSync=false).");
  //         break;
  //       }

  //       const skip = page * this.limit;
  //       const pageQuery = this.createQuery({ skip, limit: this.limit });

  //       // If your use case supports streaming via onData, keep using it:
  //       // Otherwise, just await and push the array.
  //       const chunk = await this.findObjectUseCase.execute(
  //         collection,
  //         pageQuery,
  //         {
  //           timeout: 120000,
  //         },
  //       );

  //       // normalize: some SDKs return {results: [...]} — adjust if needed
  //       const rows = Array.isArray(chunk) ? chunk : (chunk?.results ?? []);

  //       if (!rows.length) {
  //         // server-side cap or no more rows
  //         break;
  //       }

  //       buffer.push(...rows);
  //       fetched += rows.length;
  //       page += 1;

  //       // Yield to the UI thread (prevents long task blocking)
  //       await Promise.resolve();
  //     }

  //     clearInterval(flushInterval);
  //     flushBuffer();

  //     // "Not found" safeguard (if filters are very narrow)
  //     // if (this.objects.length === 0) {
  //     //   this.view.setNotFound(true);
  //     // }
  //   } catch (error) {
  //     this.view.showError(error);
  //   } finally {
  //     this.hideProgress();
  //     this.progress = false;
  //   }
  // }

  onChangeDate(where) {
    const { tran_date } = where;

    if (tran_date && tran_date.$gte && tran_date.$lte) {
      const startDate = addHours(startOfDay(parseISO(tran_date.$gte)), 5);
      const endDate = addMinutes(
        addHours(addDays(startOfDay(parseISO(tran_date.$lte)), 0), 4),
        59,
      );
      // const endDate = addMinutes(
      //   addHours(addDays(startOfDay(parseISO(tran_date.$lte)), 1), 4), // Change 0 to 1
      //   59,
      // );

      const formattedStartDate = format(startDate, "yyyy-MM-dd HH:mm:ss.SSS");
      const formattedEndDate = format(endDate, "yyyy-MM-dd HH:mm:ss.SSS");

      const startDateObj = addHours(startOfDay(parseISO(tran_date.$gte)), 5);
      const endDateObj = addMinutes(
        addHours(addDays(startOfDay(parseISO(tran_date.$lte)), 0), 4),
        59,
      );

      this.view.setState({
        range: {
          tran_date: {
            $gte: formatISO(startDateObj),
            $lte: formatISO(endDateObj),
          },
        },
      });

      this.filterDate = {
        tran_date: {
          $gte: formattedStartDate,
          $lte: formattedEndDate,
        },
      };
    }
    // this.getObjects();
  }

  onChangeStore(where) {
    this.filterStore = where;
  }

  searchButton() {
    this.getObjects();
  }

  searchSubmit(search) {
    console.log("Search parameter received:", search, "Type:", typeof search);

    // Handle different types of search input
    if (typeof search === "object" && search !== null && search.$or) {
      // This is a complex search object with $or conditions from the Search component
      // We need to extract the search term and add our items search to it

      // Find the search term from the $or array (usually in the first condition)
      let searchTerm = null;
      if (search.$or && search.$or.length > 0) {
        const firstCondition = search.$or[0];
        if (firstCondition && firstCondition.id && firstCondition.id.$regex) {
          searchTerm = firstCondition.id.$regex;
        }
      }

      // Set the search object to be merged with other conditions
      this.search = search;

      // Also set searchAny for our items search
      if (searchTerm) {
        this.searchAny = searchTerm;
      } else {
        this.searchAny = null;
      }

      console.log("Complex search object received, search term:", searchTerm);
    } else {
      // Handle simple string search or clear search
      let searchTerm = null;

      if (typeof search === "string") {
        searchTerm = search.trim();
      } else if (typeof search === "object" && search !== null) {
        // Handle other object types
        if (search.description) {
          searchTerm = String(search.description).trim();
        } else if (search.value) {
          searchTerm = String(search.value).trim();
        } else if (search.query) {
          searchTerm = String(search.query).trim();
        }
      } else if (search !== null && search !== undefined) {
        searchTerm = String(search).trim();
      }

      // Clear the complex search object
      this.search = {};

      // Handle simple search term
      if (
        !searchTerm ||
        searchTerm === "" ||
        searchTerm === "null" ||
        searchTerm === "undefined"
      ) {
        this.searchAny = null;
      } else {
        this.searchAny = searchTerm;
      }

      console.log("Simple search term:", this.searchAny);
    }

    // this.searchSync = true;
    // this.getObjects();
  }

  clearSearch() {
    this.searchAny = null;
    this.search = {};
    this.searchSync = true;
    this.getObjects();
  }

  calculateSums(objects) {
    const sumReducer = (acc, object, key) => acc + Number(object[key] || 0);

    const sum_total = objects?.reduce(
      (acc, obj) => sumReducer(acc, obj, "total"),
      0,
    );

    const sum_discount = objects?.reduce(
      (acc, obj) => sumReducer(acc, obj, "discount_amount"),
      0,
    );

    const sum_taxes = objects?.reduce(
      (acc, obj) => sumReducer(acc, obj, "total_tax"),
      0,
    );

    return {
      sum_total,
      sum_discount,
      sum_taxes,
      grossSum: sum_total + sum_discount,
      grossSales: sum_total + sum_discount - sum_taxes,
      grossSales: sum_total,
    };
  }

  calculateGrossSalesInfo() {
    const itemSold = this.objects;

    let totalAmount = 0;

    if (Array.isArray(itemSold)) {
      for (let transaction of itemSold) {
        if (transaction.items && Array.isArray(transaction.items)) {
          for (let item of transaction.items) {
            // Find the AMOUNT in customProperties
            if (item.customProperties && Array.isArray(item.customProperties)) {
              const amountProperty = item.customProperties.find(
                (prop) => prop.codeName === "UNIT_PRICE",
              );

              if (amountProperty) {
                let amount = Number(amountProperty.value);
                if (!isNaN(amount)) {
                  totalAmount += amount;
                }
              }
            }
          }
        }
      }
    } else {
      console.error("itemSold is not an array");
    }

    return {
      value: totalAmount,
    };
  }

  calculateTotalItemSoldSalesInfo() {
    const itemSold = this.objects;

    let totalQuantity = 0;

    if (Array.isArray(itemSold)) {
      for (let transaction of itemSold) {
        if (transaction.items && Array.isArray(transaction.items)) {
          for (let item of transaction.items) {
            let qty = Number(item.quantity);
            if (!isNaN(qty)) {
              totalQuantity += qty;
            }
          }
        }
      }
    } else {
      console.error("itemSold is not an array");
    }

    return {
      value: totalQuantity,
    };
  }

  calculateTotalTransactionInfo() {
    const prevTransaction = 5;
    const totalTransaction = this.objects;
    return {
      value: totalTransaction?.length,
      percentage: this.calculatePercentage(
        totalTransaction?.length,
        prevTransaction,
      ),
    };
  }

  calculateTotalGuestInfo() {
    const prevCust = 6;
    const custCount = this.countCustomers();
    return {
      value: custCount,
      percentage: this.calculatePercentage(custCount, prevCust),
    };
  }

  calculateDiscountCost() {
    const prevCust = 6;
    const discountSum = this.objects
      ?.map((cust) => Number(cust.discount_amount))
      .reduce((acc, prev) => acc + prev, 0);
    return {
      value: discountSum,
      percentage: this.calculatePercentage(discountSum, prevCust),
    };
  }

  calculatePercentage(current, previous) {
    if (previous > 0 && current !== undefined) {
      return parseFloat(((current - previous) / previous) * 100).toFixed(2);
    }
    return 0;
  }

  countCustomers() {
    return this.objects
      ?.filter((cus) => cus.cust_name !== "-")
      .map((cust) => cust.cust_name).length;
  }
}

export default ProductsPresenter;
